import type {RawBounds} from "#template/geometry-processing/bounds"
import type {MeshNodes, DataNodes} from "@cm/render-nodes"
import type {Vector2, Vector3} from "@cm/math"

export interface MaterialGroup {
    start: number
    count: number
    materialIndex: number
}

export interface ReifiedMeshData {
    contentHash: string
    vertices: Float32Array
    normals: Float32Array
    uvs: Float32Array[]
    faceIDs: Int32Array // one per triangle
    indices: Uint32Array // three per triangle, into the vertex attributes
    materialGroups: MaterialGroup[] // start and count refer to triangle indices/counts
    centroid: [number, number, number]
    surfaceArea: number
    bounds: RawBounds
}

export interface AbstractMeshData {
    contentHash: string
    displayGeometryGraph: MeshNodes.Mesh
    renderGeometryGraph: MeshNodes.Mesh
    displayGeometryGraphResources: DataNodes.EvaluationResources
}

export interface CompleteMeshData {
    reified: ReifiedMeshData
    abstract: AbstractMeshData
}

export interface MeshBuffers {
    vertices: Float32Array
    normals: Float32Array
    uvs: Float32Array[]
    faceIDs: Int32Array // one per triangle
    indices: Uint32Array | Uint16Array
    materialIndex: number
}

export type MeshPointData = {
    position: Vector3
    normal: Vector3
    uvs: Vector2[]
    materialID: number
}

/////////////////////////////////////////////////////////

export function createTrivialIndices(vertexCount: number): Uint32Array {
    const indices = new Uint32Array(vertexCount)
    for (let i = 0; i < vertexCount; i++) indices[i] = i
    return indices
}

export function faceIDOfTriangle(mesh: {indices: ArrayLike<number>; faceIDs: Int32Array}, triangle: number): number {
    const triangleCount = mesh.indices.length / 3
    if (mesh.faceIDs.length !== triangleCount) throw new Error(`${mesh.faceIDs.length} face IDs for ${triangleCount} triangles`)
    return mesh.faceIDs[triangle]
}

type MeshBufferSource = Pick<ReifiedMeshData, "vertices" | "normals" | "uvs" | "faceIDs" | "indices" | "materialGroups">

function createMeshBuffers(mesh: MeshBufferSource, group: MaterialGroup): MeshBuffers {
    const groupIndices = mesh.indices.subarray(group.start * 3, (group.start + group.count) * 3)

    let firstVertex = groupIndices.length > 0 ? Infinity : 0
    let lastVertex = -1
    for (const vertex of groupIndices) {
        if (vertex < firstVertex) firstVertex = vertex
        if (vertex > lastVertex) lastVertex = vertex
    }
    const vertexCount = lastVertex + 1 - firstVertex

    // WebGL2 always treats 0xFFFF in a 16-bit index as a primitive restart
    const indices = new (vertexCount > 65535 ? Uint32Array : Uint16Array)(groupIndices.length)
    for (let i = 0; i < groupIndices.length; i++) indices[i] = groupIndices[i] - firstVertex

    return {
        vertices: mesh.vertices.subarray(firstVertex * 3, (lastVertex + 1) * 3),
        normals: mesh.normals.subarray(firstVertex * 3, (lastVertex + 1) * 3),
        uvs: mesh.uvs.map((uv) => uv.subarray(firstVertex * 2, (lastVertex + 1) * 2)),
        faceIDs: mesh.faceIDs.subarray(group.start, group.start + group.count),
        indices,
        materialIndex: group.materialIndex,
    }
}

export function createMeshBuffersForMaterialGroups(mesh: MeshBufferSource): MeshBuffers[] {
    const triangleCount = mesh.indices.length / 3
    if (mesh.faceIDs.length !== triangleCount) throw new Error(`${mesh.faceIDs.length} face IDs for ${triangleCount} triangles`)
    return mesh.materialGroups.map((group) => createMeshBuffers(mesh, group))
}
